import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  addMonthsKeepingDayOne,
  createJstCalendarDate,
  getJstToday,
  isSameCalendarDate,
  type JstCalendarDate,
} from '@/utils/businessDate';
import { toDateString } from '@/utils/datetime';

interface StaffOnlyDatePickerProps {
  selectedDate?: string | null;
  onDateSelect: (date: string) => void;
}

export const StaffOnlyDatePicker = ({
  selectedDate,
  onDateSelect,
}: StaffOnlyDatePickerProps) => {
  const { t, i18n } = useTranslation();
  const jstToday = getJstToday();
  const [currentMonth, setCurrentMonth] = useState<JstCalendarDate>(jstToday);

  useEffect(() => {
    if (selectedDate) {
      // new Date('YYYY-MM-DD') は UTC 解釈されるため、ブラウザTZがUTC以西だと月初選択で前月にロールバックする
      const [year, month] = selectedDate.split('-').map(Number);
      setCurrentMonth(createJstCalendarDate(year, month, 1));
    }
  }, [selectedDate]);

  // 月の移動
  const changeMonth = (direction: number) => {
    setCurrentMonth((prev) => addMonthsKeepingDayOne(prev, direction));
  };

  // 月のラベル（例: 2025年11月 / November 2025）
  const getMonthLabel = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth() + 1;

    if (i18n.language === 'ja') {
      return `${year}年${month}月`;
    } else {
      const monthNames = [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December',
      ];
      return `${monthNames[currentMonth.getMonth()]} ${year}`;
    }
  };

  // カレンダーの日付配列を生成
  const generateCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();

    // 月の最初の日と最後の日
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    // 最初の日の曜日（0: 日曜日）
    const firstDayOfWeek = firstDay.getDay();

    // カレンダーに表示する日数
    const daysInMonth = lastDay.getDate();

    const days: Array<{
      date: string;
      day: number;
      isCurrentMonth: boolean;
      isToday: boolean;
      isSelected: boolean;
    }> = [];

    // 前月の日付で埋める
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = firstDayOfWeek - 1; i >= 0; i--) {
      const day = prevMonthLastDay - i;
      const date = createJstCalendarDate(year, month, day);
      days.push({
        date: toDateString(date),
        day,
        isCurrentMonth: false,
        isToday: false,
        isSelected: false,
      });
    }

    // 当月の日付（JST基準）
    const today = jstToday;

    for (let day = 1; day <= daysInMonth; day++) {
      const date = createJstCalendarDate(year, month + 1, day);
      const dateStr = toDateString(date);

      days.push({
        date: dateStr,
        day,
        isCurrentMonth: true,
        isToday: isSameCalendarDate(date, today),
        isSelected: dateStr === selectedDate,
      });
    }

    // 次月の日付で埋める（カレンダーを6週間分表示）
    const remainingDays = 42 - days.length;
    for (let day = 1; day <= remainingDays; day++) {
      const date = createJstCalendarDate(year, month + 2, day);
      days.push({
        date: toDateString(date),
        day,
        isCurrentMonth: false,
        isToday: false,
        isSelected: false,
      });
    }

    return days;
  };

  // 日付をクリック
  const handleDateClick = (dateStr: string) => {
    onDateSelect(dateStr);
  };

  const calendarDays = generateCalendarDays();

  return (
    <div className="date-picker e-date-picker">
      <div className="date-picker__header">
        <button
          className="date-picker__nav date-picker__nav--prev"
          type="button"
          onClick={() => changeMonth(-1)}
          aria-label={t('common.datePicker.previousMonth')}
        >
          ←
        </button>
        <div className="date-picker__month-label" aria-live="polite">
          {getMonthLabel()}
        </div>
        <button
          className="date-picker__nav date-picker__nav--next"
          type="button"
          onClick={() => changeMonth(1)}
          aria-label={t('common.datePicker.nextMonth')}
        >
          →
        </button>
      </div>

      <div className="date-picker__weekdays">
        <span>{t('common.weekdays.sunday')}</span>
        <span>{t('common.weekdays.monday')}</span>
        <span>{t('common.weekdays.tuesday')}</span>
        <span>{t('common.weekdays.wednesday')}</span>
        <span>{t('common.weekdays.thursday')}</span>
        <span>{t('common.weekdays.friday')}</span>
        <span>{t('common.weekdays.saturday')}</span>
      </div>

      <div
        className="date-picker__grid"
        aria-label={t('common.datePicker.calendar')}
      >
        {calendarDays.map((dayInfo, index) => {
          return (
            <button
              key={index}
              type="button"
              className={`date-picker__day-button ${
                dayInfo.isToday ? 'is-today' : ''
              } ${dayInfo.isSelected ? 'is-selected' : ''} ${
                !dayInfo.isCurrentMonth ? 'is-other-month' : ''
              }`}
              onClick={() => handleDateClick(dayInfo.date)}
              aria-label={dayInfo.date}
            >
              {dayInfo.day}
            </button>
          );
        })}
      </div>
    </div>
  );
};
